import {
    Component,
    createEffect,
    createSignal,
    onCleanup,
    onMount,
    Show,
} from 'solid-js'

import { useNavigate } from '@solidjs/router'
import { StickyBtn } from 'components'
import { UserIcon } from 'icons'
import { createStore } from 'solid-js/store'
import './style/navbar.scss'

export type sectionT = 'hero' | 'about' | 'contact'

const [section, setSection] = createSignal<sectionT>('hero')

export { section, setSection }

const Navbar: Component = () => {
    const [state, setState] = createStore({
        active: false,
        blend: false,
    })

    onMount(() => {
        let ac = new AbortController()

        document.addEventListener('scroll', handleScr, {
            passive: true,
            signal: ac.signal,
        })

        onCleanup(() => {
            ac.abort()
        })
    })

    const handleScr = () => {
        let y = scrollY

        if (y > 80) {
            setState('active', true)
        } else {
            setState('active', false)
        }

        // debug
        // change to hero height later
        if (y > innerHeight) {
            setState('blend', true)
        } else {
            setState('blend', false)
        }
    }

    return (
        <>
            <a
                class='nav-logo-wrapper'
                href='/'
                classList={{ blend: state.blend }}
            >
                <div class='logo'>
                    <img
                        src='/public/imgs/logo.png'
                        loading='lazy'
                        decoding='async'
                        alt=''
                    />
                </div>
            </a>
            <nav class='nav-container' classList={{ active: state.active }}>
                <div class='empty' />

                <Ctas />

                <PanelWrapper />
            </nav>
        </>
    )
}

const Ctas: Component = () => {
    let ctasRef!: HTMLDivElement

    let activeTabRef!: HTMLElement
    let activeLayerRef!: HTMLElement
    let prevActive: HTMLElement | null = null

    let holderTabRef!: HTMLElement
    let holderLayerRef!: HTMLElement

    let hoverBounds: DOMRect

    onMount(() => {
        handleActiveTab()

        // — attach the holder listeners to *every* nav-link —
        const links = ctasRef.querySelectorAll<HTMLElement>('.nav-link')
        links.forEach(link => {
            link.addEventListener('mouseenter', onHoverEnter)
            link.addEventListener('mousemove', onHoverMove)
            link.addEventListener('mouseleave', onHoverLeave)
        })
    })

    createEffect(() => {
        section()
        handleActiveTab()
    })

    onCleanup(() => {
        // remove hover listeners too
        const links = ctasRef.querySelectorAll<HTMLElement>('.nav-link')
        links.forEach(link => {
            link.removeEventListener('mouseenter', onHoverEnter)
            link.removeEventListener('mousemove', onHoverMove)
            link.removeEventListener('mouseleave', onHoverLeave)
        })
    })

    function scrToSec(sec: sectionT) {
        setSection(sec)

        const el = document.querySelector<HTMLElement>(`#${sec}`)

        if (!el) return

        scrollTo({
            top: el.offsetTop,
            behavior: 'smooth',
        })
    }

    function onActiveMove(e: MouseEvent) {
        const pos = activeTabRef.getBoundingClientRect()
        const mx = e.clientX - pos.left - pos.width / 2
        activeLayerRef.style.transform = `
      translate(${mx * 0.15}px, 0)
      rotate3d(${mx * -0.1}, 0, 0, 12deg)
    `
    }
    function onActiveEnter() {
        activeLayerRef.style.transition = ''
    }
    function onActiveLeave() {
        activeLayerRef.style.transform = ''
        activeLayerRef.style.transition = '0.2s ease'
    }

    function onHoverMove(e: MouseEvent) {
        const mx = e.clientX - hoverBounds.left - hoverBounds.width / 2

        holderLayerRef.style.transform = `
        translate(${mx * 0.15}px, 0)
        rotate3d(${mx * -0.1}, 0, 0, 12deg)
        `
    }
    function onHoverEnter(e: MouseEvent) {
        hoverBounds = (e.currentTarget as HTMLElement).getBoundingClientRect()

        const link = e.currentTarget as HTMLElement
        const { clientWidth: w, offsetLeft: x } = link
        // size & position the holder background immediately
        holderTabRef.style.transition = ''
        holderTabRef.style.width = `${w}px`
        holderTabRef.style.transform = `translateX(${x}px)`
        holderLayerRef.style.transition = ''
    }
    function onHoverLeave() {
        const activeLink =
            ctasRef.querySelector<HTMLElement>('.nav-link.active')
        if (!activeLink) return

        // let CSS snap it back
        holderTabRef.style.transition = 'transform 0.3s ease, width 0.3s ease'

        holderLayerRef.style.transform = ''
        holderLayerRef.style.transition = '0.2s ease'

        const { clientWidth: w, offsetLeft: left } = activeLink
        holderTabRef.style.width = `${w}px`

        let leftV = left
        if (leftV > 0) leftV = left - left / 100

        holderTabRef.style.transform = `translateX(${leftV}px)`
    }

    const handleActiveTab = () => {
        // tear down old
        if (prevActive) {
            prevActive.removeEventListener('mousemove', onActiveMove)
            prevActive.removeEventListener('mouseenter', onActiveEnter)
            prevActive.removeEventListener('mouseleave', onActiveLeave)
        }

        // wire up new active
        const link = ctasRef.querySelector<HTMLElement>('.nav-link.active')
        if (!link || !activeTabRef || !activeLayerRef) return
        link.addEventListener('mousemove', onActiveMove)
        link.addEventListener('mouseenter', onActiveEnter)
        link.addEventListener('mouseleave', onActiveLeave)

        // size & position the active slider
        const { clientWidth: w, offsetLeft: left } = link
        activeTabRef.style.width = `${w}px`

        let leftV = left
        if (leftV > 0) leftV = left - left / 100

        activeTabRef.style.transform = `translateX(${leftV}px)`

        holderTabRef.style.width = `${w}px`
        holderTabRef.style.transform = `translateX(${leftV}px)`

        prevActive = link
    }

    return (
        <div
            class={`nav-ctas ${innerWidth > 768 ? 'title_small' : 'description'}`}
            ref={e => {
                ctasRef = e

                handleActiveTab()
            }}
        >
            <div class='tabs'>
                {/* active slider */}
                <div class='active' ref={e => (activeTabRef = e)}>
                    <div class='layer' ref={e => (activeLayerRef = e)} />
                </div>

                {/* holder slider */}
                <div class='holder' ref={e => (holderTabRef = e)}>
                    <div class='layer' ref={e => (holderLayerRef = e)} />
                </div>
            </div>

            <button
                classList={{ active: section() == 'hero' }}
                class='nav-link'
                onclick={() => scrToSec('hero')}
            >
                خانه
            </button>
            <button
                classList={{ active: section() == 'about' }}
                class='nav-link'
                onclick={() => scrToSec('about')}
            >
                درباره ما
            </button>
            <button
                classList={{ active: section() == 'contact' }}
                class='nav-link'
                onclick={() => scrToSec('contact')}
            >
                ارتباط با ما
            </button>
        </div>
    )
}

const PanelWrapper: Component = () => {
    let nav = useNavigate()
    return (
        <div class='panel-wrapper'>
            <Show
                when={innerWidth > 768}
                fallback={
                    <button class='panel-cta icon' onclick={() => nav('login')}>
                        <UserIcon />
                    </button>
                }
            >
                <StickyBtn
                    label={'ورود به پنل'}
                    onclick={() => nav('login')}
                    class='panel-cta title_small'
                />
            </Show>
        </div>
    )
}

export default Navbar
